import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import { useCreateAutomaticTurboDiscountCouponMutation } from './hooks/useDiscountCouponQuery'
import { ADDITIONAL_RESOURCE_OPTIONS, VALID_FOR_OPTIONS } from './discountCouponConstants'
import { buildTodayDateInputValue, validateDueDate } from './discountCouponDateUtils'


const RECURRENCY_TYPES = [
  { key: 'MONTHLY', tag: 'Mensal' },
  { key: 'YEARLY', tag: 'Anual' },
]

function toApiDate(dateInputValue) {
  if (!dateInputValue) return ''

  const [year, month, day] = dateInputValue.split('-')
  return `${day}/${month}/${year}`
}

const MIN_DISCOUNT_PERCENTAGE = 1
const MAX_DISCOUNT_PERCENTAGE = 100

function isFilledDiscount(entry) {
  return entry?.discount !== undefined
}

function findDiscountOutOfRange(discountValues) {
  return Object.values(discountValues)
    .filter(isFilledDiscount)
    .find(
      ({ discount }) =>
        !Number.isFinite(discount) ||
        discount < MIN_DISCOUNT_PERCENTAGE ||
        discount > MAX_DISCOUNT_PERCENTAGE,
    )
}

function buildTurboDiscountsData(discountValues) {
  const allDiscounts = []

  Object.entries(discountValues).forEach(([key, entry]) => {
    if (!isFilledDiscount(entry)) return

    const [resourceType, recurrencyType] = key.split('-')
    allDiscounts.push({ resourceType, recurrencyType, value: entry.discount })
  })

  return allDiscounts
}

function buildCampaignData(form) {
  return [
    {
      discountCouponCodePrefix: form.code,
      validFor: form.validFor,
      discounts: buildTurboDiscountsData(form.discountValues),
    },
  ]
}

function buildAutomaticTurboCouponsPayload(form) {
  return {
    startDate: toApiDate(form.fromDate),
    dueDateToApply: toApiDate(form.dueDate),
    observation: form.observation ?? '',
    campaigns: buildCampaignData(form),
  }
}

function AutomaticTurboDiscountCouponAddPage() {
  const navigate = useNavigate()
  const createMutation = useCreateAutomaticTurboDiscountCouponMutation()

  const [fromDate, setFromDate] = useState(buildTodayDateInputValue())
  const [dueDate, setDueDate] = useState('')
  const [validFor, setValidFor] = useState('ONE_MONTH')
  const [discountValues, setDiscountValues] = useState({})
  const [code, setCode] = useState('')
  const [observation, setObservation] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  function notify(message) {
    setSnackbar({ open: true, message })
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  function handleDiscountChange(key, value) {
    setDiscountValues((current) => ({
      ...current,
      [key]: { discount: value === '' ? undefined : Number(value) },
    }))
  }

  const hasAtLeastOneDiscount = Object.values(discountValues).some(isFilledDiscount)
  const isValid = code.trim() !== '' && fromDate !== '' && dueDate !== '' && hasAtLeastOneDiscount

  function handleSave() {
    const dueDateError = validateDueDate(dueDate)
    if (dueDateError === 'invalid-format') return
    if (dueDateError === 'not-future') {
      notify('A expiração deve ser uma data futura')
      return
    }

    if (findDiscountOutOfRange(discountValues)) {
      notify(
        `Os percentuais de desconto devem estar entre ${MIN_DISCOUNT_PERCENTAGE} e ${MAX_DISCOUNT_PERCENTAGE}`,
      )
      return
    }

    const form = { fromDate, dueDate, validFor, discountValues, code, observation }
    const couponsPayload = buildAutomaticTurboCouponsPayload(form)

    createMutation.mutate(couponsPayload, {
      onSuccess: () => {
        navigate('/workspace/discount-coupons', {
          state: { successMessage: 'Cupons de desconto de turbo criados com sucesso!' },
        })
      },
      onError: (error) =>
        notify(
          `Erro ao cadastrar os cupons de desconto: ${error?.response?.data?.errorMessage || error?.message || ''}`,
        ),
    })
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Adicionar Cupons Automáticos de Turbo
      </Typography>

      <Paper sx={{ p: 3 }} variant="outlined">
        <Grid container spacing={2}>
          <Grid size={6}>
            <TextField
              id="fromDate"
              type="date"
              label="Data de início*"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
              required
            />
          </Grid>
          <Grid size={6}>
            <TextField
              id="dueDate"
              type="date"
              label="Aplicação expira em*"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
              required
            />
          </Grid>

          <Grid size={12}>
            <TextField
              id="validFor"
              select
              label="Válido a partir da aplicação por*"
              value={validFor}
              onChange={(event) => setValidFor(event.target.value)}
              fullWidth
            >
              {VALID_FOR_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {ADDITIONAL_RESOURCE_OPTIONS.map((resource) => (
            <Grid key={resource.value} size={12} sx={{ mb: 2 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>{resource.label}</strong>
                </Typography>

                <Grid container spacing={2}>
                  {RECURRENCY_TYPES.map((recurrency) => {
                    const key = `${resource.value}-${recurrency.key}`

                    return (
                      <Grid key={key} size={{ xs: 12, sm: 6 }}>
                        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                          <InputLabel htmlFor={key} sx={{ minWidth: 56 }}>
                            {recurrency.tag}
                          </InputLabel>
                          <TextField
                            id={key}
                            type="number"
                            placeholder="porcentagem (%)"
                            value={discountValues[key]?.discount ?? ''}
                            onChange={(event) => handleDiscountChange(key, event.target.value)}
                            slotProps={{ htmlInput: { min: 1, max: 100, step: 'any' } }}
                            sx={{ maxWidth: 180, width: '100%' }}
                          />
                        </Stack>
                      </Grid>
                    )
                  })}
                </Grid>
              </Paper>
            </Grid>
          ))}

          <Grid size={12}>
            <TextField
              id="code"
              label="Prefixo do cupom*"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid size={12}>
            <TextField
              id="observation"
              label="Observações"
              value={observation}
              onChange={(event) => setObservation(event.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Grid>

          <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={() => navigate('/workspace/discount-coupons')}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isValid || createMutation.isPending}
            >
              Salvar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AutomaticTurboDiscountCouponAddPage
